import { Alert, ExternalLink, Page, Skeleton, Text } from '@ui/themed';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { SparkSuspense } from '../../../components/spark-suspense';
import { useEnv } from '../../../context/env-context';
import { useHandlers, withPageProviders } from '../../../context/page-context';
import { useAllPriceLists } from '../../price-lists/hooks/use-price-lists';
import type { PriceListApiResponse } from '../../price-lists/types';
import { useVariantLookup } from '../../products/hooks/use-variant-lookup';
import { AddPriceList } from '../components/add-price-list';
import { PriceListPricingCard } from '../components/price-list-pricing-card';
import { VariantSummaryCard } from '../components/variant-summary-card';
import { DOCS_URLS, I18N_COMPONENTS, I18N_DETAIL_PAGE, PRICE_EDITOR_PATH } from '../constants';
import { availablePriceListsToAdd, nextDraftQuantity } from '../editor-rules';
import { useVariantPricing } from '../hooks/use-variant-pricing';
import type { DraftRow, VariantPriceInput, VariantPricing } from '../types';

let draftId = 0;
const nextDraftId = () => `draft-${++draftId}`;

const blankDraft = (quantity: number): DraftRow => ({
  id: nextDraftId(),
  quantity,
  price: 0,
  tax_type: 'net',
});

export const VariantPricingPage = withPageProviders(() => {
  const env = useEnv();
  const { navigationHandler } = useHandlers();
  const { variantId } = navigationHandler.params();

  const { parent, variant, selectedVariantSku, isLoading, isError, refetch, notFound } =
    useVariantLookup(variantId);

  const { data: pricing, isLoading: pricingLoading } = useVariantPricing(selectedVariantSku);
  const { data: priceLists, isLoading: listsLoading } = useAllPriceLists();

  const [draftLists, setDraftLists] = useState<string[]>([]);
  const [draftRowsByList, setDraftRowsByList] = useState<Record<string, DraftRow[]>>({});

  // A variant change navigates to a new URL; the draft state belongs to the previous variant.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the reset is intentionally keyed to the selected variant
  useEffect(() => {
    setDraftLists([]);
    setDraftRowsByList({});
  }, [selectedVariantSku]);

  const handleVariantSelect = (sku: string) => {
    navigationHandler.navigate(`${PRICE_EDITOR_PATH}/${encodeURIComponent(sku)}`, {
      replace: true,
    });
  };

  const savedPricesFor = (slug: string): VariantPriceInput[] =>
    pricing?.find((list) => list.price_list_slug === slug)?.pricing ?? [];

  const addDraftRow = (slug: string) => {
    setDraftRowsByList((prev) => {
      const saved = savedPricesFor(slug);
      const drafts = prev[slug] ?? [];
      return { ...prev, [slug]: [...drafts, blankDraft(nextDraftQuantity(saved, drafts))] };
    });
  };

  const removeDraftRow = (slug: string, index: number) => {
    setDraftRowsByList((prev) => {
      const drafts = prev[slug] ?? [];
      return { ...prev, [slug]: drafts.filter((_, i) => i !== index) };
    });
  };

  const addDraftList = (slug: string) => {
    setDraftLists((prev) => [...prev, slug]);
    setDraftRowsByList((prev) => ({ ...prev, [slug]: [blankDraft(1)] }));
  };

  const removeDraftList = (slug: string) => {
    setDraftLists((prev) => prev.filter((existing) => existing !== slug));
    setDraftRowsByList((prev) => {
      const next = { ...prev };
      delete next[slug];
      return next;
    });
  };

  const draftSaved = (slug: string) => {
    setDraftLists((prev) => prev.filter((existing) => existing !== slug));
  };

  const combinedPricing: VariantPricing[] = [
    ...(pricing ?? []),
    ...draftLists.map((slug) => ({ price_list_slug: slug, pricing: [] })),
  ];
  const availableLists = availablePriceListsToAdd(priceLists, combinedPricing);
  const isEmpty = (pricing?.length ?? 0) === 0 && draftLists.length === 0;

  const renderListCard = (slug: string, priceList: PriceListApiResponse, isDraft: boolean) => (
    <PriceListPricingCard
      key={slug}
      sku={selectedVariantSku ?? ''}
      priceList={priceList}
      prices={isDraft ? [] : savedPricesFor(slug)}
      draftRows={draftRowsByList[slug] ?? []}
      isDraft={isDraft}
      onAddDraftRow={() => addDraftRow(slug)}
      onRemoveDraftRow={(index) => removeDraftRow(slug, index)}
      onDraftSaved={() => draftSaved(slug)}
      onRemoveDraftList={() => removeDraftList(slug)}
    />
  );

  return (
    <Page
      title={t(`${I18N_DETAIL_PAGE}.title`)}
      description={t(`${I18N_DETAIL_PAGE}.description`, { sku: selectedVariantSku ?? '' })}
      backUrl={PRICE_EDITOR_PATH}
    >
      {notFound ? (
        <Alert variant="warning" headingLevel="h2">
          {t(`${I18N_DETAIL_PAGE}.not_found`, { sku: selectedVariantSku ?? '' })}
        </Alert>
      ) : (
        <SparkSuspense
          isLoading={isLoading}
          isError={isError}
          errorMessage={t('errors:generic')}
          onReload={() => void refetch()}
        >
          {parent && variant && (
            <VariantSummaryCard
              product={parent}
              variant={variant}
              selectedVariantSku={selectedVariantSku ?? ''}
              onVariantSelect={handleVariantSelect}
              env={env}
            />
          )}
        </SparkSuspense>
      )}

      {pricingLoading || listsLoading ? (
        <Skeleton />
      ) : (
        <>
          {(pricing ?? []).map((list) => {
            const priceList = priceLists.find(
              (candidate) => candidate.slug === list.price_list_slug,
            );
            return priceList ? renderListCard(list.price_list_slug, priceList, false) : null;
          })}

          {draftLists.map((slug) => {
            const priceList = priceLists.find((candidate) => candidate.slug === slug);
            return priceList ? renderListCard(slug, priceList, true) : null;
          })}

          {isEmpty && (
            <Text variant="secondary">{t(`${I18N_COMPONENTS}.price_list_pricing_card.empty`)}</Text>
          )}

          <AddPriceList availableLists={availableLists} onAdd={addDraftList} />

          <Text variant="secondary" size="sm">
            {t(`${I18N_COMPONENTS}.manual_lists_note.text`)}{' '}
            <ExternalLink link={DOCS_URLS.priceEditor} showIcon>
              {t('links:learn_more')}
            </ExternalLink>
          </Text>
        </>
      )}
    </Page>
  );
});
